<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';

const props = defineProps({
  documentType: { type: String, default: 'CNH' },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['captured', 'failed', 'unsupported']);

const loading = ref(false);
const captureTarget = ref('');
const statusMessage = ref('');
const hasLoadedVendor = ref(false);
const cameraBoxStyle = {
  position: 'relative',
  width: '100%',
  height: '320px',
};
const captureButtonLabel = 'Capturar';
const captureHintLabel = 'Selecione uma captura abaixo.';
const selfieLabel = 'Selfie';
const documentLabel = 'Documento';

const scriptSources = [
  '/acessobio/js/plataform.js',
  '/acessobio/js/adapter-latest.js',
  '/acessobio/js/face-api.js',
  '/acessobio/js/app-plugin.js',
];

const documentTypeMap = {
  CNH: 'CNH',
  RG: 'RG',
  CPF: 'CPF',
  NEW_RG: 'NEW_RG',
  RG_FRONT: 'RG_FRONT',
  RG_BACK: 'RG_BACK',
};

const isCapturing = computed(() => Boolean(captureTarget.value));

const loadStylesheet = href => {
  if (document.querySelector(`link[href="${href}"]`)) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
};

const loadScript = src =>
  new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing?.dataset.loaded === 'true') {
      resolve();
      return;
    }

    const script = existing || document.createElement('script');
    script.src = src;
    script.async = false;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error(`Falha ao carregar ${src}`));
    if (!existing) document.body.appendChild(script);
  });

const ensureVendor = async () => {
  if (hasLoadedVendor.value) return;

  loadStylesheet('/acessobio/css/style.css');
  await scriptSources.reduce(
    (promise, src) => promise.then(() => loadScript(src)),
    Promise.resolve()
  );
  hasLoadedVendor.value = true;
};

const normalizeCapture = payload => ({
  target: captureTarget.value,
  base64: payload?.base64 || '',
  base64Back: payload?.base64Back || '',
  log: payload?.Log || payload?.log || {},
  documentType: props.documentType,
});

const stopCamera = () => {
  if (!window.stream) return;

  window.stream.getTracks().forEach(track => track.stop());
  window.stream = null;
};

const createWebFrame = () => {
  const AcessoWebFrame = window.AcessoWebFrame;
  if (!AcessoWebFrame) {
    throw new Error('AcessoBio WebFrame indisponível.');
  }

  const webFrame = new AcessoWebFrame();
  webFrame.acessoWebFrameModel.getHostUrlBase = path =>
    `${window.location.origin}/acessobio/${path}`;
  webFrame.onSuccessCaptureJS = payload => {
    const normalized = normalizeCapture(payload);
    emit('captured', normalized);
    statusMessage.value =
      normalized.target === 'selfie'
        ? 'Selfie capturada.'
        : 'Documento capturado.';
    captureTarget.value = '';
  };
  webFrame.onFailedCaptureJS = error => {
    const message = typeof error === 'string' ? error : error?.message;
    statusMessage.value = message || 'Não foi possível capturar.';
    emit('failed', {
      target: captureTarget.value,
      message: statusMessage.value,
    });
    captureTarget.value = '';
  };
  webFrame.onBrowserNotSupportJS = info => {
    statusMessage.value = 'Navegador sem suporte para captura biométrica.';
    emit('unsupported', info);
    captureTarget.value = '';
  };
  return webFrame;
};

const documentTypeConstant = webFrame => {
  const key = documentTypeMap[props.documentType] || 'CNH';
  return webFrame.TYPE_DOCUMENT[key] || webFrame.TYPE_DOCUMENT.CNH;
};

const startCapture = async target => {
  if (props.disabled || loading.value || captureTarget.value) return;

  loading.value = true;
  captureTarget.value = target;
  statusMessage.value =
    target === 'selfie'
      ? 'Abrindo câmera para selfie...'
      : 'Abrindo câmera para documento...';
  try {
    await ensureVendor();
    stopCamera();
    const webFrame = createWebFrame();
    if (target === 'selfie') {
      await webFrame.acessoWebFrameModel.loadModelsCameraInteligence();
      webFrame.initCameraInteligence('#2557D6', '#F59E0B', '#FFFFFF');
    } else {
      webFrame.initDocument(documentTypeConstant(webFrame), '#FFFFFF');
    }
  } catch (error) {
    const message = error?.message || 'Não foi possível iniciar a captura.';
    statusMessage.value = message;
    emit('failed', { target, message });
    captureTarget.value = '';
  } finally {
    loading.value = false;
  }
};

onBeforeUnmount(() => {
  stopCamera();
});
</script>

<template>
  <div class="grid gap-3">
    <div
      id="box-camera"
      class="relative min-h-[300px] overflow-hidden rounded-lais border border-lais-border bg-lais-bg-soft"
      :style="cameraBoxStyle"
    >
      <button
        id="camera--trigger"
        type="button"
        :aria-label="captureButtonLabel"
        class="grid place-items-center bg-lais-primary text-white"
      >
        <span class="i-lucide-camera size-5" aria-hidden="true" />
      </button>
      <div
        id="box--loading"
        class="absolute inset-0 z-20 hidden place-items-center bg-white/90"
      >
        <span
          class="i-lucide-loader-circle size-6 animate-spin text-lais-primary"
          aria-hidden="true"
        />
      </div>
      <div id="box--orientation" class="hidden" />
      <div id="message" />
      <div
        v-if="!isCapturing"
        class="absolute inset-0 z-10 grid place-items-center px-4 text-center text-[13px] text-lais-text-muted"
      >
        {{ captureHintLabel }}
      </div>
    </div>

    <div class="grid gap-2 sm:grid-cols-2">
      <button
        type="button"
        class="inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text transition-colors hover:border-lais-primary hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="disabled || loading || Boolean(captureTarget)"
        @click="startCapture('selfie')"
      >
        <span class="i-lucide-scan-face size-4" aria-hidden="true" />
        {{ selfieLabel }}
      </button>
      <button
        type="button"
        class="inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text transition-colors hover:border-lais-primary hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="disabled || loading || Boolean(captureTarget)"
        @click="startCapture('document')"
      >
        <span class="i-lucide-id-card size-4" aria-hidden="true" />
        {{ documentLabel }}
      </button>
    </div>
    <p v-if="statusMessage" class="text-[12px] text-lais-text-muted">
      {{ statusMessage }}
    </p>
  </div>
</template>
